import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authFetch, logout } from '../lib/authFetch';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import DispatchNav from '../components/DispatchNav';
import {
  AlertCircle,
  Calendar,
  CheckCircle,
  ChevronRight,
  Clock,
  ListChecks,
  LogOut,
  Phone,
  RefreshCw,
  Search,
  TrendingUp,
  UserRoundCheck,
  XCircle,
  Loader2,
} from 'lucide-react';

/**
 * Mobile-first dispatch operations dashboard.
 *
 *   Cards → each opens /dispatch/bookings pre-filtered.
 *   Short lists → newest submissions / today's Confirmed / needs-action.
 *   Header → America/Chicago clock, refresh, secure logout, search.
 *
 * All counts come from real booking data via GET /api/dispatch/dashboard-summary.
 */
const DispatchDashboard = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const [now, setNow] = useState(new Date());
  const [search, setSearch] = useState('');
  const [user, setUser] = useState(null);

  const fetchSummary = useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true); else setLoading(true);
    setError('');
    try {
      const res = await authFetch('/api/dispatch/dashboard-summary');
      if (!res.ok) {
        setError(`Unable to load dashboard (HTTP ${res.status}).`);
        return;
      }
      setData(await res.json());
    } catch (e) {
      setError('Network error while loading dashboard.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    // Auth is verified server-side on the first data fetch below. If the
    // session cookie is missing or expired, authFetch will redirect to login.
    (async () => {
      try {
        const meRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/me`, {
          credentials: 'include',
        });
        if (meRes.ok) {
          setUser(await meRes.json());
        }
      } catch { /* ignore */ }
    })();
    fetchSummary();
    const clockId = setInterval(() => setNow(new Date()), 30_000);
    const refreshId = setInterval(() => fetchSummary(true), 60_000);
    return () => { clearInterval(clockId); clearInterval(refreshId); };
  }, [navigate, fetchSummary]);

  const handleLogout = async () => {
    await logout();
  };

  const handleSearch = (e) => {
    e.preventDefault();
    const q = search.trim();
    if (!q) return;
    navigate(`/dispatch/bookings?q=${encodeURIComponent(q)}`);
  };

  const openList = (filter) =>
    navigate(`/dispatch/bookings?filter=${encodeURIComponent(filter)}`);

  const chicagoClock = now.toLocaleString('en-US', {
    timeZone: 'America/Chicago',
    weekday: 'short', month: 'short', day: 'numeric',
    hour: 'numeric', minute: '2-digit', timeZoneName: 'short',
  });

  if (loading && !data) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center" data-testid="dashboard-loading">
          <Loader2 className="mx-auto w-8 h-8 animate-spin text-[#004C97]" />
          <p className="mt-3 text-gray-600 text-sm">Loading dashboard…</p>
        </div>
      </div>
    );
  }

  const c = data?.counts || {};
  const cards = [
    { key: 'pending', label: 'Pending Dispatch Review', value: c.pending_dispatch_review,
      icon: AlertCircle, tone: 'amber', filter: 'Pending Dispatch Review' },
    { key: 'contacted', label: 'Contacted', value: c.contacted,
      icon: Phone, tone: 'sky', filter: 'Contacted' },
    { key: 'confirmed', label: 'Confirmed', value: c.confirmed,
      icon: CheckCircle, tone: 'blue', filter: 'Confirmed' },
    { key: 'today', label: "Today's Trips", value: c.todays_trips,
      icon: Calendar, tone: 'emerald', filter: 'today' },
    { key: 'upcoming', label: 'Upcoming Trips', value: c.upcoming_trips,
      icon: TrendingUp, tone: 'indigo', filter: 'upcoming' },
    { key: 'declined', label: 'Declined or Cancelled', value: c.declined_or_cancelled,
      icon: XCircle, tone: 'rose', filter: 'Declined,Cancelled' },
  ];

  return (
    <div className="min-h-screen bg-gray-50 pb-10">
      {/* Header — dispatch-only chrome, no public site nav */}
      <header className="bg-[#004C97] shadow-sm sticky top-0 z-20">
        <div className="max-w-6xl mx-auto px-3 sm:px-6 py-3">
          <div className="flex items-center justify-between gap-2">
            <div className="min-w-0">
              <h1 className="text-white text-lg sm:text-xl font-semibold truncate">
                MoveraCare Dispatch
              </h1>
              <p className="text-[11px] sm:text-xs text-white/70 truncate" data-testid="dashboard-clock">
                {chicagoClock}
              </p>
            </div>
            <div className="flex items-center gap-1 sm:gap-2">
              <Button
                onClick={() => fetchSummary(true)}
                variant="ghost"
                className="text-white hover:bg-white/10 min-h-[44px] px-2"
                data-testid="dashboard-refresh-btn"
                title="Refresh"
              >
                <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
              </Button>
              <Button
                onClick={handleLogout}
                variant="ghost"
                className="text-white hover:bg-white/10 min-h-[44px] px-2"
                data-testid="dashboard-logout-btn"
                title="Log out"
              >
                <LogOut className="w-4 h-4 sm:mr-1" />
                <span className="hidden sm:inline">Log out</span>
              </Button>
            </div>
          </div>
          {user && (
            <p className="text-[11px] text-white/70 mt-1" data-testid="dashboard-user">
              Signed in as <span className="font-semibold text-white">{user.full_name || user.username}</span> · {user.role}
            </p>
          )}
        </div>
      </header>

      <DispatchNav />

      <main className="max-w-6xl mx-auto px-3 sm:px-6 py-4 sm:py-6 space-y-4">
        {/* Quick search */}
        <form onSubmit={handleSearch} className="flex gap-2" data-testid="dashboard-search-form">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by ref, customer, or phone"
              className="pl-9 min-h-[48px]"
              data-testid="dashboard-search-input"
            />
          </div>
          <Button
            type="submit"
            className="min-h-[48px] px-4 bg-[#F9A825] hover:bg-[#E08E1E] text-white"
            data-testid="dashboard-search-btn"
          >
            Search
          </Button>
        </form>

        {/* Nav to full list */}
        <div className="flex flex-wrap gap-2">
          <Button
            onClick={() => navigate('/dispatch/bookings')}
            className="min-h-[44px] bg-[#004C97] hover:bg-[#003a78] text-white"
            data-testid="dashboard-view-all-btn"
          >
            <ListChecks className="w-4 h-4 mr-2" />
            Transportation Requests
          </Button>
        </div>

        {error && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800" data-testid="dashboard-error">
            {error}
          </div>
        )}

        {/* 6 summary cards */}
        <section aria-label="Dispatch summary" className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="dashboard-cards">
          {cards.map((card) => (
            <SummaryCard key={card.key} card={card} onOpen={() => openList(card.filter)} />
          ))}
        </section>

        {/* Three short lists */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <ShortListCard
            title="Newest Requests"
            emptyLabel="No new requests yet."
            items={data?.newest || []}
            testid="dashboard-newest"
            onOpen={(id) => navigate(`/dispatch/bookings/${id}`)}
          />
          <ShortListCard
            title="Today's Confirmed Trips"
            emptyLabel="No confirmed trips scheduled today."
            items={data?.todays_confirmed || []}
            testid="dashboard-today"
            onOpen={(id) => navigate(`/dispatch/bookings/${id}`)}
            showTime
          />
          <ShortListCard
            title="Requires Action"
            emptyLabel="Nothing awaiting review."
            items={data?.needs_action || []}
            testid="dashboard-needs-action"
            onOpen={(id) => navigate(`/dispatch/bookings/${id}`)}
          />
        </div>

        <p className="text-[11px] text-gray-500 text-center">
          Last updated {new Date(data?.generated_at || Date.now()).toLocaleTimeString()} · auto-refresh every 60 s
        </p>
      </main>
    </div>
  );
};

const TONE_MAP = {
  amber:   { bg: 'bg-amber-50',   text: 'text-amber-800',   ring: 'ring-amber-200' },
  sky:     { bg: 'bg-sky-50',     text: 'text-sky-800',     ring: 'ring-sky-200' },
  blue:    { bg: 'bg-blue-50',    text: 'text-blue-800',    ring: 'ring-blue-200' },
  emerald: { bg: 'bg-emerald-50', text: 'text-emerald-800', ring: 'ring-emerald-200' },
  indigo:  { bg: 'bg-indigo-50',  text: 'text-indigo-800',  ring: 'ring-indigo-200' },
  rose:    { bg: 'bg-rose-50',    text: 'text-rose-800',    ring: 'ring-rose-200' },
};

function SummaryCard({ card, onOpen }) {
  const Icon = card.icon;
  const tone = TONE_MAP[card.tone] || TONE_MAP.sky;
  return (
    <button
      type="button"
      onClick={onOpen}
      data-testid={`dashboard-card-${card.key}`}
      className={`text-left rounded-xl border border-gray-200 bg-white shadow-sm hover:shadow-md hover:border-[#004C97] transition-all p-3 min-h-[100px]`}
    >
      <div className="flex items-start justify-between">
        <span className={`inline-flex w-8 h-8 items-center justify-center rounded-lg ring-1 ${tone.bg} ${tone.text} ${tone.ring}`}>
          <Icon className="w-4 h-4" />
        </span>
        <ChevronRight className="w-4 h-4 text-gray-300" />
      </div>
      <p className="mt-2 text-2xl font-bold text-[#004C97]" data-testid={`dashboard-card-${card.key}-value`}>
        {typeof card.value === 'number' ? card.value : '—'}
      </p>
      <p className="text-xs text-gray-600 leading-snug">{card.label}</p>
    </button>
  );
}

function ShortListCard({ title, items, emptyLabel, onOpen, testid, showTime }) {
  return (
    <Card>
      <CardContent className="p-4" data-testid={testid}>
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">{title}</h2>
          <UserRoundCheck className="w-4 h-4 text-gray-300" />
        </div>
        {items.length === 0 ? (
          <p className="text-sm text-gray-500" data-testid={`${testid}-empty`}>{emptyLabel}</p>
        ) : (
          <ul className="divide-y divide-gray-100">
            {items.map((b) => (
              <li key={b.id}>
                <button
                  type="button"
                  onClick={() => onOpen(b.id)}
                  className="w-full text-left py-2 flex items-center justify-between gap-2 min-h-[44px] hover:bg-gray-50 rounded"
                  data-testid={`${testid}-item-${b.id}`}
                >
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-[#004C97] truncate">{b.ref}</p>
                    <p className="text-xs text-gray-600 truncate">
                      {b.patient_name || 'Patient'} · {b.date}{showTime && b.time ? ` · ${b.time}` : ''}
                    </p>
                  </div>
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-700 whitespace-nowrap">
                    {b.status}
                  </span>
                  <ChevronRight className="w-4 h-4 text-gray-300 flex-shrink-0" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

export default DispatchDashboard;
